import type React from 'react';

const CAPITAL_PLANNER_URL = 'https://www.gofmx.com/capital-planner/';

const BENCHMARKING_QUESTIONS = [
  'How do our test scores, graduation rate and student-to-staff ratio compare to peers?',
  'Are we spending more on capital projects, or carrying more debt per student, than our peers?',
  'How does our facilities operations spending per student compare to districts like ours?',
] as const;

/**
 * A card in the results side rail (Figma "Ready for development"). On wide screens the rail sits
 * flush against the page's right edge, so cards round only their left corners; the first one
 * also butts against the top.
 */
export const railCard =
  'overflow-hidden rounded-[var(--fmxe-border-radius-md)] bg-[var(--fmxe-color-sky-blue-light)] shadow-[0_0_10px_0_var(--fmxe-color-gray-light-1)] lg:rounded-r-none lg:first:rounded-tl-none';

const cardTitle =
  'text-[length:var(--fmxe-font-family-size-h4)] font-bold leading-[var(--fmxe-font-family-line-height-h4)] text-[var(--fmxe-color-gray-core)]';
const cardBody =
  'text-[length:var(--fmxe-font-family-size-xs)] leading-[var(--fmxe-font-family-line-height-xs)] text-[var(--fmxe-color-gray-light-3)]';

/** Figma's "button text/md": orange label with a trailing arrow. */
export function RailLink({ href, external, children }: { href: string; external?: boolean; children: React.ReactNode }) {
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="inline-flex items-center rounded-[var(--fmxe-border-radius-round)] px-[var(--fmxe-space-xs)] text-base font-bold leading-[var(--fmxe-font-family-line-height-h5)] text-[var(--fmxe-color-orange)] hover:underline"
    >
      {children}
      {/* The Figma arrow is drawn pointing left; the design flips it. */}
      {/* eslint-disable-next-line @next/next/no-img-element -- static SVG icon */}
      <img src="/images/grants/arrow-right.svg" alt="" width={20} height={20} className="size-5 rotate-180" />
    </a>
  );
}

export function RailCardText({ title, body, titleWidth }: { title: string; body: string; titleWidth?: string }) {
  return (
    <div className="flex flex-col gap-[var(--fmxe-space-xs)]">
      <h3 className={`${cardTitle} ${titleWidth ?? ''}`}>{title}</h3>
      <p className={cardBody}>{body}</p>
    </div>
  );
}

/**
 * The results page's side rail: FMX products that pick up where the grant report leaves off.
 * `appOrigin` points signup at the canonical host, so visitors on a legacy host (io.gofmx.com)
 * sign up on the one we're keeping.
 */
export function GrantsPromos({ email, appOrigin = '' }: { email?: string; appOrigin?: string }) {
  const signupHref = `${appOrigin}/signup${email ? `?email=${encodeURIComponent(email)}` : ''}`;
  return (
    <>
      <section className={railCard}>
        <div className="flex flex-col items-start gap-[var(--fmxe-space-md)] p-[var(--fmxe-space-lg)]">
          <RailCardText
            title="Turn grant dollars into a plan."
            titleWidth="max-w-[215px]"
            body="Competitive grants ask what shape your buildings are in and what your plan is. FMX Capital Planner gives you the answer."
          />
          <RailLink href={CAPITAL_PLANNER_URL} external>
            Explore Capital Planner
          </RailLink>
        </div>
        {/* Cropped to the chart's top-left, bleeding off the card's right and bottom edges, as designed. */}
        <div aria-hidden className="relative ml-auto h-[161px] w-[314px] max-w-full overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element -- static marketing image */}
          <img
            src="/images/grants/capital-planner-cost-funds.png"
            alt=""
            width={3581}
            height={2099}
            className="absolute left-[4.41%] top-0 h-[124.56%] w-[108.83%] max-w-none"
          />
        </div>
      </section>

      <section className={railCard}>
        <div className="flex flex-col items-start gap-[var(--fmxe-space-md)] p-[var(--fmxe-space-lg)]">
          <RailCardText title="Unlock more insights with FMX Benchmarking." body="Benchmark your funding against similar districts for free." />
          <ul className="flex w-full flex-col gap-[var(--fmxe-space-sm)]">
            {BENCHMARKING_QUESTIONS.map((q) => (
              <li
                key={q}
                className="rounded-[var(--fmxe-border-radius-sm)] bg-[var(--fmxe-color-sky-blue-light-1)] p-[var(--fmxe-space-md)] text-[length:var(--fmxe-font-family-size-xs)] leading-[var(--fmxe-font-family-line-height-xs)] text-[var(--fmxe-color-gray-core)]"
              >
                {q}
              </li>
            ))}
          </ul>
          <RailLink href={signupHref}>Explore FMX Benchmarking tool</RailLink>
        </div>
      </section>
    </>
  );
}
